<ng-template #template let-anchor>
    <span>{{ anchor.nativeElement.innerText }}</span>
</ng-template>
<div kendoTooltip showOn="none" [tooltipTemplate]="template" (mouseover)="showTooltip($event)">
    <kendo-grid
        class="rounded-lg border-0 shadow-md"
        #grid
        id="{{ id }}"
        [data]="gridData"
        [skip]="state.skip"
        [pageable]="pageable"
        [pageSize]="state.take"
        [sortable]="sortable"
        [selectable]="selectable"
        [size]="gridSize"
        [height]="gridHeight"
        [scrollable]="scrollable"
        [reorderable]="reorderable"
        [resizable]="resizable"
        [sort]="state.sort"
        [loading]="data?.isLoading || false"
        [kendoGridSelectBy]="selectionKey"
        [filter]="state.filter"
        [rowClass]="rowClass"
        [navigable]="true"
        [hideHeader]="hideHeader"
        [(selectedKeys)]="selection"
        (dataStateChange)="onDataStateChanged($event)"
        (pageChange)="onPageChange($event)"
        (sortChange)="onSortChanged($event)"
        (selectedKeysChange)="selectedKeysChange.emit($event)"
        (cellClick)="cellClick.emit($event)"
        (cellClose)="cellClose.emit($event)"
        (columnReorder)="onColumnChange()"
        (columnResize)="onColumnChange()"
        (columnLockedChange)="onColumnChange()"
        (columnVisibilityChange)="onColumnChange()"
        (detailExpand)="onDetailExpanded($event)">
        @if (showNativeGrid) {
            @if (showToolbar) {
                <ng-template kendoGridToolbarTemplate>
                    <ng-content select="[toolbar]" />
                    @for (section of buttons; track $index) {
                        @if (['Actions', 'Export'].includes(section.label)) {
                            @for (button of section?.items; track $index) {
                                @if (resolveProperty(button?.isVisible)) {
                                    <app-button
                                        class="pr-2"
                                        [variant]="button?.variant"
                                        [disabled]="resolveProperty(button?.isDisabled)"
                                        [tooltip]="button?.label.toString() | translate"
                                        [icon]="button?.icon"
                                        (clicked)="button?.onClick()">
                                        @if (!resolveProperty(button?.iconOnly)) {
                                            <span class="leading-3">
                                                {{ button?.label.toString() | translate }}
                                            </span>
                                        }
                                    </app-button>
                                }
                            }
                        }
                    }
                    <kendo-grid-spacer />
                    <ng-content select="[toolbarCenter]" />
                    <kendo-grid-spacer />
                    @if (allowGridSave) {
                        @if (saveAndLoadButtonsVisible) {
                            <app-button
                                tooltip="{{ 'Save View' | translate }} "
                                variant="success"
                                icon="faSolidCheck"
                                (clicked)="onSaveGridSettings()" />
                        }
                        @if (savedStateExists && saveAndLoadButtonsVisible) {
                            <app-button
                                tooltip="{{ 'Cancel View Changes' | translate }}"
                                variant="warning"
                                icon="faSolidBan"
                                (clicked)="loadGridSettings()" />
                        }
                        @if (savedStateExists) {
                            <app-button
                                tooltip="{{ 'Default View' | translate }}"
                                icon="faSolidArrowsRotate"
                                variant="primary"
                                (clicked)="clearGridSettings()" />
                        }
                    }
                    <app-button class="!p-0" tooltip="{{ 'Columns' | translate }} " variant="light">
                        <kendo-grid-column-chooser [autoSync]="true" />
                    </app-button>
                    @if (allowGridSave) {
                        @if (isGridCompressed) {
                            <app-button
                                class="ml-2"
                                variant="light"
                                icon="faSolidExpand"
                                tooltip="{{ 'Expand' | translate }}"
                                (clicked)="toggleCompression()" />
                        }
                        @if (!isGridCompressed) {
                            <app-button
                                class="ml-2"
                                variant="light"
                                icon="faSolidCompress"
                                tooltip="{{ 'Compress' | translate }}"
                                (clicked)="toggleCompression()" />
                        }
                    }
                    @if (filterable) {
                        <app-button
                            tooltip="Filter"
                            icon="faSolidFilter"
                            [variant]="nrOfActiveFilters ? 'primary' : 'light'"
                            (clicked)="toggleFilter.emit()">
                            @if (nrOfActiveFilters) {
                                <span class="leading-3">{{ nrOfActiveFilters }}</span>
                            }
                        </app-button>
                    }
                    @if (nrOfActiveFilters) {
                        <app-button
                            variant="primary"
                            icon="faSolidXmark"
                            tooltip="Clear Filter"
                            (clicked)="clearFilter.emit()" />
                    }
                </ng-template>
            }
            @if (isSelectable) {
                <kendo-grid-checkbox-column [showSelectAll]="showSelectAll" [width]="40" />
            }
            @if (!hasContentProjection) {
                @for (column of gridSettings?.columnsConfig; track column) {
                @if (column.isVisible) {
                    <kendo-grid-column
                        [title]="column.title | translate"
                        [field]="column.field"
                        [width]="column.width"
                        [class]="column.class"
                        [sortable]="column.isSortable"
                        [format]="column.format"
                        [style]="column.style"
                        [hidden]="column.isHidden">
                        @if (column.columnHeaderTooltip) {
                            <ng-template kendoGridHeaderTemplate>
                                <div
                                    class="flex w-full items-center justify-between gap-2"
                                    kendoTooltip
                                    title="{{ column.columnHeaderTooltip.text | translate }}">
                                    {{ column.title | translate }}
                                    <ng-container *ngIf="column.columnHeaderTooltip.showIcon">
                                        <ng-icon
                                            size="0.75rem"
                                            [class]="
                                                column.columnHeaderTooltip.iconClass ||
                                                'text-primary-foreground opacity-75'
                                            "
                                            [name]="column.columnHeaderTooltip.icon || 'faSolidCircleInfo'"></ng-icon>
                                    </ng-container>
                                </div>
                            </ng-template>
                        }
                        <ng-template kendoGridCellTemplate let-row="dataItem">
                            @if (column.isLink) {
                                <app-grid-cell-template
                                    [column]="column"
                                    [defaultViewMode]="defaultViewMode"
                                    [parentRoute]="parentRoute"
                                    [row]="row" />
                            } @else {
                                <app-grid-cell-template [column]="column" [row]="row">
                                    @if (row[column.indicator?.field] === true) {
                                        <span title="{{ column.indicator?.tooltip | translate }}">
                                            @if (column.indicator?.route) {
                                                <app-link
                                                    [route]="[
                                                        column.indicator?.route?.path,
                                                        row[column.indicator?.route?.field]
                                                    ]"
                                                    [queryParams]="column.indicator?.route?.queryParams">
                                                    <ng-icon
                                                        [ngClass]="column.indicator?.class"
                                                        [name]="column.indicator?.icon" />
                                                </app-link>
                                            } @else {
                                                <ng-icon
                                                    [ngClass]="column.indicator?.class"
                                                    [name]="column.indicator?.icon" />
                                            }
                                        </span>
                                    }
                                </app-grid-cell-template>
                            }
                        </ng-template>
                    </kendo-grid-column>
                }
            }
            }
        }
        <kendo-grid-messages
            pagerItems="{{ 'items' | translate }}"
            pagerItemsPerPage="{{ 'items per page' | translate }}"
            noRecords="{{ 'No records available' | translate }}"
            pagerOf="{{ 'of' | translate }}" />
    </kendo-grid>
</div>
